import { ControlLibrary } from '../js/external-js/variableModules/namespaces/ControlLibrary.namespace';
import { db } from '../js/external-js/config/main_config';

type IsModuleEnabled = (moduleName: string) => boolean;

// Define interface for control data
export interface ControlData {
  value: string;        // controlID
  label: string;        // controlName
  description: string;  // controlDescription
  standard: string;     // Which standard it belongs to
  metadata: Record<string, any>; // Additional metadata
}

// Cache for controls to avoid repeated API calls
let controlsCache: Record<string, ControlData[]> = {};

/**
 * Get clauses for a specific ISO standard (if available)
 */
export const getClausesByStandard = async (standard: string): Promise<ControlData[]> => {
  try {
    return await getISOClauses(standard);
  } catch (e) {
    console.error('[ControlsService] Error getting clauses for', standard, e);
    return [];
  }
};
let lastFetchTime: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

// Define all possible standards
const ALL_STANDARDS = [
  'ISO 27001',
  'SOC 2',
  'NIST 800-53',
  'Essential 8',
  'Privacy Act',
  'DISP',
  'ISM',
  'QGOV-IS18',
  'SMB1001',
  'ISO 9001',
  'ISO 45001',
  'ISO 14001',
  'ISO 42001',
  'ISO 27701'
];

/**
 * Get all standards available in the system
 * @param isModuleEnabled Function to check if a module is enabled
 * @returns Array of standard names
 */
export const getStandards = async (isModuleEnabled: IsModuleEnabled): Promise<string[]> => {
  console.log('[ControlsService] getStandards called with isModuleEnabled:', !!isModuleEnabled);

  try {
    if (!isModuleEnabled) {
      console.warn('[ControlsService] isModuleEnabled function not provided, returning empty standards list');
      return [];
    }

    // Log all standards being checked
    console.log('[ControlsService] Checking all standards:', ALL_STANDARDS);

    // Filter standards based on enabled modules
    const filtered = ALL_STANDARDS.filter(standard => {
      // Map standard names to their module names
      const moduleMap: Record<string, string> = {
        'ISO 27001': 'iso27001',
        'SOC 2': 'soc2',
        'NIST 800-53': 'nist80053',
        'Essential 8': 'essential8',
        'Privacy Act': 'privacyact',
        'DISP': 'disp',
        'ISM': 'ism',
        'SMB1001': 'smb1001',
        'qgov-is18': 'qgov-is18',
        'QGOV-IS18': 'qgov-is18',
        'ISO 9001': 'iso9001',
        'ISO 45001': 'iso45001',
        'ISO 14001': 'iso14001',
        'ISO 42001': 'iso42001',
        'ISO 27701': 'iso27701'
      };

      const moduleName = moduleMap[standard];
      const isEnabled = moduleName ? isModuleEnabled(moduleName) : false;

      console.log(`[ControlsService] Checking standard: ${standard}`, {
        moduleName,
        isEnabled,
        isModuleEnabled: !!isModuleEnabled
      });

      return isEnabled;
    });

    console.log('[ControlsService] Filtered standards:', filtered);
    return filtered;
  } catch (error) {
    console.error('[ControlsService] Error getting standards:', error);
    return [];
  }
};

/**
 * Get all enabled standards for the current company
 * @param enabledModules Array of enabled module names
 * @returns Array of enabled standard names
 */
export const getEnabledStandards = async (enabledModules: string[] = []): Promise<string[]> => {
  try {
    if (!Array.isArray(enabledModules)) {
      console.warn('[ControlsService] enabledModules is not an array, returning empty standards list');
      return [];
    }

    // Map between module names and their display names
    const moduleToStandardMap: Record<string, string> = {
      'iso27001': 'ISO 27001',
      'soc2': 'SOC 2',
      'nist80053': 'NIST 800-53',
      'essential8': 'Essential 8',
      'privacyact': 'Privacy Act',
      'disp': 'DISP',
      'ism': 'ISM',
      'smb1001': 'SMB1001',
      'qgov-is18': 'QGOV-IS18',
      'QGOV-IS18': 'QGOV-IS18',
      'iso9001': 'ISO 9001',
      'iso45001': 'ISO 45001',
      'iso14001': 'ISO 14001',
      'iso42001': 'ISO 42001',
      'iso27701': 'ISO 27701'
    };

    // Convert enabled modules to standard names
    const standards = enabledModules
      .map(module => moduleToStandardMap[module.toLowerCase()])
      .filter(Boolean) as string[]; // Filter out any undefined values

    console.log('[ControlsService] Enabled standards:', standards);
    return standards;
  } catch (error) {
    console.error('[ControlsService] Error getting enabled standards:', error);
    return [];
  }
};

/**
 * Gets both controls and clauses for a given standard
 * @param standard The standard name
 * @returns Combined array of controls and clauses
 */
export const getControlsAndClauses = async (standard: string): Promise<ControlData[]> => {
  const [controls, clauses] = await Promise.all([
    getControlsByStandard(standard),
    getISOClauses(standard)
  ]);

  // Combine and deduplicate by control ID
  const combined = [...controls];
  const existingIds = new Set(controls.map(c => c.value));

  for (const clause of clauses) {
    if (!existingIds.has(clause.value)) {
      combined.push(clause);
    }
  }

  return combined;
};


/**
 * Fetches and processes ISO clauses for a given standard
 * @param standard The ISO standard name (e.g., 'ISO 9001', 'ISO 27001')
 * @returns Array of ControlData objects for the clauses
 */
const getISOClauses = async (standard: string): Promise<ControlData[]> => {
  const clauseMapping: Record<string, { path: string, year: string }> = {
    'ISO 27001': { path: 'ISO27001', year: '2022' },
    'ISO 9001': { path: 'ISO9001', year: '2015' },
    'ISO 45001': { path: 'ISO45001', year: '2018' },
    'ISO 14001': { path: 'ISO14001', year: '2015' },
    'ISO 42001': { path: 'ISO42001', year: '2023' },
    'ISO 27701': { path: 'ISO27701', year: '2019' }
  };

  const mapping = clauseMapping[standard];
  if (!mapping) {
    return [];
  }

  try {
    const clausesDoc = await db
      .collection('compliance')
      .doc('isoClauses')
      .collection(mapping.path)
      .doc(mapping.year)
      .get();

    if (!clausesDoc.exists) {
      console.warn(`No clauses found for ${standard} ${mapping.year}`);
      return [];
    }

    const clausesData = clausesDoc.data()?.clauses || {};
    return Object.entries(clausesData).map(([id, clause]: [string, any]) => ({
      value: id,
      label: clause.controlName || id,
      description: clause.implementationGuidance || '',
      standard: standard,
      metadata: {
        ...clause,
        isClause: true,
        type: 'clause'
      }
    }));
  } catch (error) {
    console.error(`Error fetching clauses for ${standard}:`, error);
    return [];
  }
};

/**
 * Get controls for a specific standard
 * @param standard Standard name to fetch controls for
 * @returns Array of control data
 */
export const getControlsByStandard = async (standard: string): Promise<ControlData[]> => {
  // If we have cached controls for this standard and they're still fresh, return them
  if (controlsCache[standard] && Date.now() - lastFetchTime < CACHE_DURATION) {
    return controlsCache[standard];
  }

  const isISOStandard = [
    'ISO 9001', 'ISO 45001', 'ISO 14001', 'ISO 42001', 'ISO 27701'
  ].includes(standard);

  if (isISOStandard) {
    const clauses = await getISOClauses(standard);
    controlsCache[standard] = clauses;
    lastFetchTime = Date.now();
    return clauses;
  }

  try {
    const controls: ControlData[] = [];
    const companyID = (window as any).companyID;

    // Map standard names to document IDs
    const documentMapping: Record<string, string[]> = {
      'ISO 27001': ['iso270012022AnnexAControls', 'iso270012022Controls'],
      'SOC 2': ['SOC2Controls'],
      'NIST 800-53': ['nist800-53-rev5'],
      'Essential 8': ['ism'],
      'Privacy Act': ['paControls'],
      'DISP': ['DISPControls'],
      'ISM': ['ism'],
      'SMB1001': ['SMB1001Controls'],
      'QGOV-IS18': ['qgov-is18'],
      'ISO 9001': ['iso90012015AnnexAControls', 'iso90012015Controls'], // this is a clause and is actually located in /compliance/isoClauses/ISO9001/2015
      'ISO 45001': ['iso450012018AnnexAControls', 'iso450012018Controls'], // this is a clause and is actually located in /compliance/isoClauses/ISO45001/2018
      'ISO 14001': ['iso140012015AnnexAControls', 'iso140012015Controls'], // this is a clause and is actually located in /compliance/isoClauses/ISO14001/2015
      'ISO 42001': ['iso420012018AnnexAControls', 'iso420012018Controls'], // this is a clause and is actually located in /compliance/isoClauses/ISO42001/2023
      'ISO 27701': ['iso277012017AnnexAControls', 'iso277012017Controls'] // this is a clause and is actually located in /compliance/isoClauses/ISO27701/2019
    };

    // Get document IDs for the requested standard
    const docIds = documentMapping[standard] || [];

    // Fetch controls for each document
    for (const docId of docIds) {
      // Handle special cases for ISM/Essential 8 (stored in subcollections)
      if (docId === 'ism') {
        if (standard === 'Essential 8') {
          // Essential 8 controls have maturity levels
          const e8Level1 = await db
            .collection('compliance')
            .doc('ism')
            .collection('controls')
            .where('ml1', '==', 'Yes')
            .get();

          const e8Level2 = await db
            .collection('compliance')
            .doc('ism')
            .collection('controls')
            .where('ml2', '==', 'Yes')
            .get();

          const e8Level3 = await db
            .collection('compliance')
            .doc('ism')
            .collection('controls')
            .where('ml3', '==', 'Yes')
            .get();

          // Process each level
          [e8Level1, e8Level2, e8Level3].forEach((snapshot, index) => {
            snapshot.forEach(doc => {
              const data = doc.data();
              controls.push({
                value: data.controlID,
                label: data.description || data.controlName,
                description: data.topic || data.controlDescription,
                standard: `Essential 8 (ML${index + 1})`,
                metadata: {
                  ...data,
                  level: `ML${index + 1}`
                }
              });
            });
          });
        } else {
          // Regular ISM controls
          const snapshot = await db
            .collection('compliance')
            .doc('ism')
            .collection('controls')
            .get();

          snapshot.forEach(doc => {
            const data = doc.data();
            controls.push({
              value: data.controlID,
              label: data.description || data.controlName,
              description: data.topic || data.controlDescription,
              standard: 'ISM',
              metadata: data
            });
          });
        }
      } else if (docId === 'SMB1001Controls') {
        // Handle SMB1001 controls which have a different structure and include level information
        const docSnapshot = await db
          .collection('compliance')
          .doc(docId)
          .get();

        if (docSnapshot.exists) {
          const docData = docSnapshot.data();

          // The controls are in a map directly in the document
          if (docData && docData.Controls) {
            // Convert the controls map to an array
            Object.entries(docData.Controls).forEach(([controlId, controlData]: [string, any]) => {
              controls.push({
                value: controlId,
                label: controlData.controlName || controlData.name || controlId,
                description: controlData.controlDescription || controlData.description || '',
                standard: `SMB1001${controlData.level ? ` (${controlData.level})` : ''}`,
                metadata: {
                  ...controlData,
                  level: controlData.level || ''
                }
              });
            });
          }
        }
      } else {
        // Handle all other standards where controls are in the document itself as a map
        const docSnapshot = await db
          .collection('compliance')
          .doc(docId)
          .get();

        if (docSnapshot.exists) {
          const docData = docSnapshot.data();

          // The controls are in a map directly in the document
          if (docData && typeof docData === 'object') {
            // Convert the controls map to an array
            Object.entries(docData).forEach(([controlId, controlData]: [string, any]) => {
              controls.push({
                value: controlId,
                label: controlData.controlName || controlData.name || controlId,
                description: controlData.controlDescription || controlData.description || '',
                standard: standard,
                metadata: controlData
              });
            });
          }
        }
      }
    }

    // Cache the results
    controlsCache[standard] = controls;
    lastFetchTime = Date.now();

    return controls;
  } catch (error) {
    console.error(`[ControlsService] Error fetching controls for ${standard}:`, error);
    return [];
  }
};

/**
 * Get all controls for all enabled standards
 * @param isModuleEnabled Function to check if a module is enabled
 * @returns Array of control data
 */
export const getAllControls = async (isModuleEnabled: IsModuleEnabled): Promise<ControlData[]> => {
  try {
    // Check if we have a complete cache
    const allCachedControls = Object.values(controlsCache).flat();
    if (allCachedControls.length > 0 && Date.now() - lastFetchTime < CACHE_DURATION) {
      console.log('[ControlsService] Returning cached controls:', {
        count: allCachedControls.length,
        allCachedControls,
        lastFetchTime,
        now: Date.now(),
        cacheDuration: CACHE_DURATION
      });
      return allCachedControls;
    }

    // Get all available standards, not just enabled ones
    // This ensures we have a complete dataset
    const standards = await getStandards(isModuleEnabled);
    const allControls: ControlData[] = [];

    // Fetch controls for each standard
    for (const standard of standards) {
      console.log(`[ControlsService] Fetching controls for ${standard}`);
      const controls = await getControlsByStandard(standard);
      console.log(`[ControlsService] Found ${controls.length} controls for ${standard}`);
      allControls.push(...controls);
    }

    console.log(`[ControlsService] Total controls found: ${allControls.length}`);
    console.log('[ControlsService] Controls:', allControls);    
    return allControls;
  } catch (error) {
    console.error('[ControlsService] Error fetching all controls:', error);
    return [];
  }
};

// In-memory search index to speed up searches
let searchIndex: Map<string, Set<string>> = new Map();
let isIndexBuilt = false;

/**
 * Build a search index for faster searches
 */
const buildSearchIndex = (controls: ControlData[]) => {
  if (isIndexBuilt) return;

  console.log('[ControlsService] Building search index for', controls.length, 'controls');
  searchIndex.clear();

  // Process each control
  controls.forEach(control => {
    // Skip invalid controls
    if (!control.value) return;

    // Helper function to index a field
    const indexField = (field: string | undefined, isControlId: boolean = false) => {
      if (!field) return;

      // For control IDs, we want to preserve the full ID as a token
      // as well as index its parts for more flexible searching
      if (isControlId) {
        // Add the complete control ID as a token (preserving dots, numbers, etc.)
        const fullId = field.toLowerCase();
        if (!searchIndex.has(fullId)) {
          searchIndex.set(fullId, new Set());
        }
        searchIndex.get(fullId)?.add(control.value);
      }

      // Tokenize the field (split into words)
      const tokens = field.toLowerCase()
        .replace(/[^\w\s]/g, ' ') // Replace non-alphanumeric with spaces
        .split(/\s+/)
        .filter(token => token.length > 2); // Only index tokens with 3+ chars

      // Add each token to the index
      tokens.forEach(token => {
        if (!searchIndex.has(token)) {
          searchIndex.set(token, new Set());
        }
        searchIndex.get(token)?.add(control.value);
      });
    };

    // Index the main fields
    indexField(control.value, true); // Mark control.value as a control ID
    indexField(control.label);
    indexField(control.description);

    // Index metadata fields that are strings
    if (control.metadata && typeof control.metadata === 'object') {
      Object.values(control.metadata).forEach(value => {
        if (typeof value === 'string') {
          indexField(value);
        }
      });
    }
  });

  console.log('[ControlsService] Search index built with', searchIndex.size, 'tokens');
  isIndexBuilt = true;
};

/**
 * Reset the search index when data changes
 */
export const resetSearchIndex = () => {
  searchIndex.clear();
  isIndexBuilt = false;
};

/**
 * Search for controls based on a query
 * @param query Search query
 * @param standardFilter Optional standard to filter by
 * @param page Optional page number for pagination (1-based)
 * @param pageSize Optional page size for pagination
 * @returns Object with results array and pagination info
 */
export const searchControls = async (
  query: string,
  standardFilter?: string,
  page: number = 1,
  pageSize: number = 50,
  isModuleEnabled: IsModuleEnabled
): Promise<{ results: ControlData[], total: number, page: number, pageSize: number, pages: number }> => {
  try {
    console.log(`[ControlsService] Searching controls with query: "${query}"${standardFilter ? ` in standard: ${standardFilter}` : ''}`);
    const startTime = performance.now();

    // Get all controls or filtered by standard
    let controls: ControlData[] = [];

    if (standardFilter) {
      console.log(`[ControlsService] Fetching controls and clauses for standard: ${standardFilter}`);
      
      // Get both controls and clauses for the standard
      const [standardControls, standardClauses] = await Promise.all([
        getControlsByStandard(standardFilter).catch(err => {
          console.error(`[ControlsService] Error fetching controls for ${standardFilter}:`, err);
          return [];
        }),
        // Check if this is an ISO standard that might have clauses
        standardFilter.startsWith('ISO ') ? getISOClauses(standardFilter).catch(err => {
          console.error(`[ControlsService] Error fetching clauses for ${standardFilter}:`, err);
          return [];
        }) : Promise.resolve([])
      ]);
      
      // Combine and deduplicate controls and clauses by value
      const combined = [...standardControls];
      const existingValues = new Set(standardControls.map(c => c.value));
      
      for (const clause of standardClauses) {
        if (!existingValues.has(clause.value)) {
          combined.push(clause);
          existingValues.add(clause.value);
        }
      }
      
      controls = combined;
      console.log(`[ControlsService] Found ${standardControls.length} controls and ${standardClauses.length} clauses for standard: ${standardFilter} (${controls.length} total)`);
    } else {
      // Otherwise, get all controls from all standards
      controls = await getAllControls(isModuleEnabled);
      console.log(`[ControlsService] Found ${controls.length} controls across all standards`);
    }

    // If no query, return paginated controls (filtered by standard if specified)
    if (!query || !query.trim()) {
      const total = controls.length;
      const pages = Math.ceil(total / pageSize);
      const startIndex = (page - 1) * pageSize;
      const endIndex = Math.min(startIndex + pageSize, total);

      return {
        results: controls.slice(startIndex, endIndex),
        total,
        page,
        pageSize,
        pages
      };
    }

    // Simple direct search for all queries
    const searchQuery = query.toLowerCase();
    const matches = new Set<string>();

    // Log what we're searching for
    console.log(`[ControlsService] Performing direct search for: "${searchQuery}"`);

    controls.forEach(control => {
      if (!control.value) return;

      // Check if any field contains the search query
      const controlIdLower = control.value.toLowerCase();
      const labelLower = control.label ? control.label.toLowerCase() : '';
      const descLower = control.description ? control.description.toLowerCase() : '';

      // For debugging
      if (descLower.includes(searchQuery)) {
        console.log(`[ControlsService] Found match in description: ${control.value} - ${control.description}`);
      }
      if (labelLower.includes(searchQuery)) {
        console.log(`[ControlsService] Found match in label: ${control.value} - ${control.label}`);
      }

      if (
        controlIdLower.includes(searchQuery) ||
        labelLower.includes(searchQuery) ||
        descLower.includes(searchQuery)
      ) {
        matches.add(control.value);
      }
    });

    let matchingControlIds = matches;

    // If no matches, return empty result
    if (!matchingControlIds || matchingControlIds.size === 0) {
      return { results: [], total: 0, page, pageSize, pages: 0 };
    }

    // Convert matching IDs to actual control objects
    const matchingControls = controls.filter(control =>
      control.value && matchingControlIds?.has(control.value)
    );

    // Apply pagination
    const total = matchingControls.length;
    const pages = Math.ceil(total / pageSize);
    const startIndex = (page - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, total);
    const paginatedResults = matchingControls.slice(startIndex, endIndex);

    const endTime = performance.now();
    console.log(`[ControlsService] Search found ${total} matching controls in ${(endTime - startTime).toFixed(2)}ms`);

    return {
      results: paginatedResults,
      total,
      page,
      pageSize,
      pages
    };
  } catch (error) {
    console.error('[ControlsService] Error searching controls:', error);
    return { results: [], total: 0, page, pageSize, pages: 0 };
  }
};

/**
 * Find controls by their IDs
 * @param controlIds Array of control IDs to find
 * @returns Array of matching control data
 */
export const findControlsByIds = async (controlIds: string[], isModuleEnabled: IsModuleEnabled): Promise<ControlData[]> => {
  try {
    if (!isModuleEnabled) {
      console.warn('[ControlsService] isModuleEnabled function not provided, cannot find controls by IDs');
      return [];
    }

    const allControls = await getAllControls(isModuleEnabled);
    return allControls.filter(control => control.value && controlIds.includes(control.value));
  } catch (error) {
    console.error('[ControlsService] Error finding controls by IDs:', error);
    return [];
  }
};

/**
 * Clear the controls cache to force a fresh fetch
 */
export const clearControlsCache = () => {
  controlsCache = {};
  lastFetchTime = 0;
};

export default {
  getStandards,
  getEnabledStandards,
  getControlsAndClauses,
  getClausesByStandard,
  getControlsByStandard,
  getAllControls,
  searchControls,
  findControlsByIds,
  clearControlsCache
};
